import { PaletteMode, PaletteOptions } from '@mui/material';
import { GetThemeOptions } from '../theme';
import { unitySemanticDarkColorTokens, unitySemanticLightColorTokens } from '../unityTokens';
import { green, grey, red, white } from './colors';

export const button = (mode: PaletteMode, opts: GetThemeOptions): PaletteOptions['button'] => {
  if (opts.panwRebrandStubYear) {
    return mode === 'light'
      ? {
          standard: {
            primary: {
              borderColor: {},
              default: unitySemanticLightColorTokens['--semantic-bg-interactive-highlight-default'],
              disabled: unitySemanticLightColorTokens['--semantic-bg-interactive-neutral-disabled'],
              hover: unitySemanticLightColorTokens['--semantic-bg-interactive-highlight-hover'],
              text: unitySemanticLightColorTokens['--semantic-text-inverse'],
              textDisabled: unitySemanticLightColorTokens['--semantic-text-disabled'],
            },
            secondary: {
              borderColor: {},
              // our secondary button is deviating from Unity's semantics because our hierarchy is
              // slightly different. Our secondary buttons sometimes live directly on top of the page
              // background color, whereas Unity's always live on top of a container. In dark mode
              // their colors are fine, in light mode they're too low contrast
              default: unitySemanticLightColorTokens['--semantic-bg-interactive-neutral-active'],
              disabled: unitySemanticLightColorTokens['--semantic-bg-interactive-neutral-disabled'],
              hover: unitySemanticLightColorTokens['--semantic-bg-interactive-neutral-hover'],
              text: unitySemanticLightColorTokens['--semantic-text-bold'],
              textDisabled: unitySemanticLightColorTokens['--semantic-text-disabled'],
            },
            tertiary: {
              hover: unitySemanticLightColorTokens['--semantic-bg-interactive-neutral-hover'],
              text: unitySemanticLightColorTokens['--semantic-text-bold'],
              disabled: 'unset',
            },
          },
          danger: {
            primary: {
              borderColor: {},
              default: unitySemanticLightColorTokens['--semantic-bg-interactive-destructive-default'],
              disabled: unitySemanticLightColorTokens['--semantic-bg-interactive-destructive-disabled'],
              hover: unitySemanticLightColorTokens['--semantic-bg-interactive-destructive-hover'],
              text: unitySemanticLightColorTokens['--semantic-text-contrast'],
            },
            tertiary: {
              hover: unitySemanticLightColorTokens['--semantic-bg-interactive-destructive-subtle-default'],
              disabled: 'unset',
              text: unitySemanticLightColorTokens['--semantic-text-destructive'],
              textDisabled: unitySemanticLightColorTokens['--semantic-text-disabled'],
            },
          },
        }
      : {
          standard: {
            primary: {
              borderColor: {},
              default: unitySemanticDarkColorTokens['--semantic-bg-interactive-highlight-default'],
              disabled: unitySemanticDarkColorTokens['--semantic-bg-interactive-neutral-disabled'],
              hover: unitySemanticDarkColorTokens['--semantic-bg-interactive-highlight-hover'],
              text: unitySemanticDarkColorTokens['--semantic-text-inverse'],
            },
            secondary: {
              borderColor: {},
              default: unitySemanticDarkColorTokens['--semantic-bg-interactive-neutral-default'],
              disabled: unitySemanticDarkColorTokens['--semantic-bg-interactive-neutral-disabled'],
              hover: unitySemanticDarkColorTokens['--semantic-bg-interactive-neutral-hover'],
              text: unitySemanticDarkColorTokens['--semantic-text-bold'],
              textDisabled: unitySemanticDarkColorTokens['--semantic-text-disabled'],
            },
            tertiary: {
              hover: unitySemanticDarkColorTokens['--semantic-bg-interactive-neutral-hover'],
              text: unitySemanticDarkColorTokens['--semantic-text-default'],
              disabled: 'unset',
            },
          },
          danger: {
            primary: {
              borderColor: {},
              default: unitySemanticDarkColorTokens['--semantic-bg-interactive-destructive-default'],
              disabled: unitySemanticDarkColorTokens['--semantic-bg-interactive-destructive-disabled'],
              hover: unitySemanticDarkColorTokens['--semantic-bg-interactive-destructive-hover'],
              text: unitySemanticDarkColorTokens['--semantic-text-contrast'],
              textDisabled: unitySemanticDarkColorTokens['--semantic-text-disabled'],
            },
            tertiary: {
              hover: unitySemanticDarkColorTokens['--semantic-bg-interactive-destructive-subtle-default'],
              disabled: 'unset',
              text: unitySemanticDarkColorTokens['--semantic-text-destructive'],
              textDisabled: unitySemanticDarkColorTokens['--semantic-text-disabled'],
            },
          },
        };
  }

  return mode === 'light'
    ? {
        standard: {
          primary: {
            borderColor: {},
            default: green[600],
            disabled: grey[50],
            hover: green[800],
            text: grey[50],
            textDisabled: grey[300],
          },
          secondary: {
            borderColor: {
              default: grey[300],
              disabled: grey[300],
              hover: grey[600],
            },
            default: white,
            hover: grey[50],
            text: grey[950],
            textDisabled: grey[300],
          },
          tertiary: {
            hover: grey[100],
            text: grey[500],
          },
        },
        danger: {
          primary: {
            borderColor: {},
            default: red[600],
            disabled: grey[50],
            hover: red[700],
            text: grey[50],
            textDisabled: grey[300],
          },
          tertiary: {
            hover: red[100],
            text: red[600],
            textDisabled: grey[300],
          },
        },
      }
    : {
        standard: {
          primary: {
            borderColor: {},
            default: green[500],
            disabled: grey[800],
            hover: green[300],
            text: grey[950],
            textDisabled: grey[500],
          },
          secondary: {
            borderColor: {
              default: grey[600],
              disabled: grey[600],
              hover: grey[200],
            },
            default: grey[900],
            hover: grey[800],
            text: grey[50],
            textDisabled: grey[500],
          },
          tertiary: {
            hover: 'transparent',
            text: grey[200],
          },
        },
        danger: {
          primary: {
            borderColor: {},
            default: red[500],
            disabled: grey[800],
            hover: red[100],
            text: grey[950],
            textDisabled: grey[500],
          },
          tertiary: {
            hover: red[800],
            text: red[500],
            textDisabled: grey[500],
          },
        },
      };
};
